<div class="organization-section">
  <mat-expansion-panel [expanded]="false">
    <mat-expansion-panel-header>
      <mat-panel-title>
        <span translate="COMPANY.FORM.ORGANIZATION_DETAILS">BridgePay Organization Details</span>
      </mat-panel-title>
    </mat-expansion-panel-header>

    <div class="organization-content">
      <!-- Loading State -->
      <div *ngIf="isLoading" class="loading-state">
        <mat-spinner diameter="40"></mat-spinner>
        <p>Loading organization details...</p>
      </div>

      <!-- Organization Info Section -->
      <div *ngIf="!isLoading" class="organization-info">
        <p class="info-text">
          Organizations allow you to group and manage paypoints. For example, organizations empower
          Knostos companies (pharmacies) to manage their own paypoint portfolios and process
          payments securely.
        </p>

        <!-- Create Organization Section -->
        <div *ngIf="!organizationExists" class="create-section">
          <mat-card class="info-card">
            <mat-card-content>
              <mat-icon color="primary">info</mat-icon>
              <p>
                No organization exists yet. Create one to start managing paypoints and processing
                payments.
              </p>
            </mat-card-content>
          </mat-card>

          <button
            mat-raised-button
            color="primary"
            (click)="createOrganization()"
            [disabled]="isLoading"
            class="create-organization-button"
          >
            <span translate="COMPANY.FORM.CREATE_ORGANIZATION">Create Organization</span>
          </button>
        </div>

        <!-- Organization Details Section -->
        <div *ngIf="organizationExists && organizationData" class="organization-details">
          <!-- Basic Information -->
          <mat-card class="details-card">
            <mat-card-header>
              <mat-card-title>Organization Information</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="detail-row">
                <label>Organization Name:</label>
                <span>{{ organizationData.name }}</span>
              </div>
              <div class="detail-row">
                <label>Organization Code:</label>
                <span class="code">{{ organizationData.organization_code }}</span>
              </div>
              <div class="detail-row">
                <label>Company ID:</label>
                <span class="code">{{ organizationData.knostos_company_id }}</span>
              </div>
              <div class="detail-row" *ngIf="organizationData.reply_to_email">
                <label>Reply-to Email:</label>
                <span>{{ organizationData.reply_to_email }}</span>
              </div>
            </mat-card-content>
          </mat-card>

          <!-- SDK Keys Section -->
          <mat-card class="sdk-keys-card">
            <mat-card-header>
              <mat-card-title>SDK Authentication Keys</mat-card-title>
              <button
                mat-icon-button
                [matTooltip]="'Generate new SDK keys'"
                (click)="generateSDKKeys()"
                [disabled]="isGeneratingKeys"
              >
                <mat-icon>refresh</mat-icon>
              </button>
            </mat-card-header>

            <mat-card-content>
              <!-- API Key (New Format) -->
              <div class="key-section" *ngIf="organizationData.api_key || showPrivateKey">
                <label>API Key (Bearer Authentication)</label>
                <div class="key-display" *ngIf="organizationData.api_key && !showPrivateKey">
                  <code
                    >{{ organizationData.api_key.substring(0, 10) }}...{{
                      organizationData.api_key.substring(organizationData.api_key.length - 4)
                    }}</code
                  >
                  <mat-hint>API key is set but partially hidden for security</mat-hint>
                </div>
              </div>

              <!-- Legacy Public Key (for backwards compatibility) -->
              <div
                class="key-section"
                *ngIf="!organizationData.api_key && organizationData.sdk_public_key"
              >
                <label>Public Key (Legacy)</label>
                <div class="key-display">
                  <code>{{ organizationData.sdk_public_key }}</code>
                  <button
                    mat-icon-button
                    (click)="copyToClipboard(organizationData.sdk_public_key, 'Public key')"
                    matTooltip="Copy public key"
                  >
                    <mat-icon>content_copy</mat-icon>
                  </button>
                </div>
                <mat-hint class="warning-hint"
                  >Legacy authentication - Please generate new API key</mat-hint
                >
              </div>

              <!-- No Keys Message -->
              <div
                class="key-section"
                *ngIf="
                  !organizationData.api_key && !organizationData.sdk_public_key && !showPrivateKey
                "
              >
                <div class="key-display empty">
                  <span class="text-muted">No API keys generated yet</span>
                </div>
              </div>

              <!-- API Key / Private Key (one-time display) -->
              <div class="key-section" *ngIf="showPrivateKey">
                <mat-card class="warning-card">
                  <mat-icon color="warn">warning</mat-icon>
                  <div>
                    <strong>Important:</strong> Copy this
                    {{ privateKeyValue.startsWith('bp_') ? 'API key' : 'private key' }} now. For
                    security reasons, it will not be shown again.
                  </div>
                </mat-card>

                <label>{{ privateKeyValue.startsWith('bp_') ? 'API Key' : 'Private Key' }}</label>
                <div class="key-display sensitive">
                  <code>{{ privateKeyValue }}</code>
                  <button
                    mat-icon-button
                    (click)="copyPrivateKey()"
                    matTooltip="Copy private key"
                    [color]="keysCopied ? 'primary' : ''"
                  >
                    <mat-icon>{{ keysCopied ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </div>

                <mat-progress-bar
                  mode="determinate"
                  [value]="timeoutProgress"
                  color="warn"
                ></mat-progress-bar>
                <small class="timer-text"
                  >This key will be hidden in {{ remainingTime }} seconds</small
                >

                <div class="key-actions">
                  <button mat-stroked-button (click)="downloadKeys()">
                    <mat-icon>download</mat-icon>
                    Download Keys
                  </button>
                  <button mat-button color="warn" (click)="clearPrivateKey()">Hide Now</button>
                </div>
              </div>

              <!-- After private key is hidden (Legacy) -->
              <div
                class="key-section"
                *ngIf="
                  !showPrivateKey && !organizationData.api_key && organizationData.sdk_public_key
                "
              >
                <label>Private Key (Legacy)</label>
                <div class="key-display">
                  <code>••••••••••••••••••••••••••••••••</code>
                  <mat-hint>Private key is set but hidden for security</mat-hint>
                </div>
              </div>

              <!-- Generate Keys Button -->
              <div class="generate-keys-section">
                <!-- New API Key Generation -->
                <button
                  mat-raised-button
                  color="primary"
                  (click)="generateAPIKey()"
                  [disabled]="isGeneratingKeys || showPrivateKey"
                >
                  <mat-spinner *ngIf="isGeneratingKeys" diameter="20"></mat-spinner>
                  <mat-icon *ngIf="!isGeneratingKeys">vpn_key</mat-icon>
                  {{ organizationData.api_key ? 'Regenerate API Key' : 'Generate API Key' }}
                </button>

                <!-- Legacy SDK Keys Generation (hidden by default) -->
                <button
                  mat-button
                  *ngIf="!organizationData.api_key"
                  (click)="generateSDKKeys()"
                  [disabled]="isGeneratingKeys || showPrivateKey"
                >
                  Generate Legacy SDK Keys
                </button>

                <mat-hint
                  *ngIf="
                    (organizationData.api_key || organizationData.sdk_public_key) && !showPrivateKey
                  "
                  class="warning-hint"
                >
                  Warning: Regenerating keys will invalidate existing keys
                </mat-hint>
              </div>
            </mat-card-content>
          </mat-card>

          <!-- Basis Theory Integration Info -->
          <mat-card class="integration-card">
            <mat-card-header>
              <mat-card-title>Payment Processing Integration</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="integration-info">
                <mat-icon color="primary">security</mat-icon>
                <div>
                  <p><strong>PCI Compliant Processing</strong></p>
                  <p class="info-text">
                    This organization is configured with secure Basis Theory applications for
                    PCI-compliant payment processing:
                  </p>
                  <ul>
                    <li>Backend application for server-side payment operations</li>
                    <li>Public application for secure frontend card tokenization</li>
                    <li>All sensitive data is encrypted and handled securely</li>
                  </ul>
                </div>
              </div>

              <div class="detail-row" *ngIf="organizationData.bridgepay_pci_api_key">
                <label>PCI API Key (Public):</label>
                <div class="key-display">
                  <code>{{ organizationData.bridgepay_pci_api_key }}</code>
                  <button
                    mat-icon-button
                    (click)="copyToClipboard(organizationData.bridgepay_pci_api_key, 'PCI API key')"
                    matTooltip="Copy PCI API key"
                  >
                    <mat-icon>content_copy</mat-icon>
                  </button>
                </div>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </div>
    </div>
  </mat-expansion-panel>
</div>
